import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { TextField } from 'mui-rff';
import { Form } from 'react-final-form';
import { FORM_ERROR } from 'final-form';
import { DataGrid } from '@mui/x-data-grid';
import {
  Alert,
  Box,
  Stack,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ListItem,
  Typography,
  Tooltip,
  ListItemText,
} from '@mui/material';
import styled from '@emotion/styled';
import {
  Downloading as DownloadingIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';

import MultiSelect from '@components/MultiSelect';
import { breadcrumbsActions } from '@store/breadcrumbs';
import { showSuccessMessage, showErrorMessage } from '@store/dashboard';
import ProdConfirmationDialog from '@components/ProdConfirmationDialog';

import {
  getUserManagementUserList,
  createUser,
  updateUser,
  deleteUser,
  selectIsLoadingUserManagementUsersList,
  selectUserManagementUsersList,
} from '../../store/user-management-users.slice';
import {
  getUserManagementRoleList,
  selectUserManagementRolesList,
  selectIsLoadingUserManagementRolesList,
} from '../../store/user-management-roles.slice';
import ApplicationPageTemplate from '../../../dashboard/components/ApplicationPageTemplate/ApplicationPageTemplate';
import TableToolbar from '../../../../components/TableToolbar';
import { userListBreadcrumb } from '../../user-management.breadcrumbs';

const MESSAGES = {
  CreateSuccess: 'Yay! A new user has been created successfully.',
  CreateFailure: 'Oops! Something went wrong while creating the user.',
  UpdateSuccess: 'Yay! User details has been updated successfully.',
  UpdateFailure: 'Oops! Something went wrong while updating the user details.',
  DeleteSuccess: 'Yay! User has been deleted successfully.',
  DeleteFailure: 'Oops! Something went wrong while deleting the user.',
  FormFieldRequiredError: 'This is a required field',
  FormFieldUsernameLengthError: 'Username should be longer than 4 characters',
  FormFieldEmailError: 'Invalid email format',
};

const MultiSelectLoadingContainer = styled(Stack)`
  flex-direction: row;
  gap: 4px;
  justify-content: space-between;
  border: 1px solid #c4c4c4;
  padding: 16px 14px;
  border-radius: 4px;
`;

function Toolbar({ openNewUserDialog, permissions }) {
  return (
    <TableToolbar>
      <Button
        variant="contained"
        disabled={!permissions?.canCreateUserManagementUser}
        onClick={openNewUserDialog}
        startIcon={<AddIcon />}>
        New
      </Button>
    </TableToolbar>
  );
}

export default function UserManagementUserList({ permissions = {} }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const userList = useSelector(selectUserManagementUsersList);
  const isLoadingUserList = useSelector(selectIsLoadingUserManagementUsersList);
  const userManagementRolesList = useSelector(selectUserManagementRolesList);
  const isLoadingRoleList = useSelector(selectIsLoadingUserManagementRolesList);

  const [isNewUserDialogOpen, setIsNewUserDialogOpen] = useState(false);
  const [formValues, setFormValues] = useState({});
  const [isEditMode, setIsEditMode] = useState(false);
  const [openDialog, setOpenDialog] = useState({ show: false });

  useEffect(() => {
    if (!permissions?.canViewUserManagementUser) {
      navigate('/landing');
    }
  }, [navigate, permissions]);

  // Breadcrumbs
  useEffect(() => {
    dispatch(breadcrumbsActions.showMenu(true));
    dispatch(breadcrumbsActions.setBreadcrumbs(userListBreadcrumb));
  }, [dispatch]);

  // API: Get User List
  useEffect(() => {
    dispatch(getUserManagementUserList());
  }, [dispatch]);

  const resetForm = useCallback(() => {
    setIsNewUserDialogOpen(false);
    setFormValues({});
    setIsEditMode(false);
  }, []);

  const onDeleteUser = () => {
    const { id } = formValues;

    const successMsg = MESSAGES.DeleteSuccess;
    const failureMsg = MESSAGES.DeleteFailure;

    dispatch(deleteUser({ id })).then(({ payload: response }) => {
      if (!response || response.status !== 'OK') {
        const message = response?.message || failureMsg;
        dispatch(showErrorMessage({ message, duration: 5000 }));

        return { [FORM_ERROR]: message };
      }

      dispatch(showSuccessMessage(successMsg));
      dispatch(getUserManagementUserList());

      return resetForm();
    });
  };
  const createNewUser = (values) => {
    const { username, email, roles = [] } = values;
    const { id } = formValues;

    const roleList = roles.map(({ id: roleId }) => roleId);

    const payload = {
      username,
      email,
      password: 'abc', // TODO: Remove after checking with backend
      roles: roleList,
    };

    let action = createUser(payload);
    let successMsg = MESSAGES.CreateSuccess;
    let failureMsg = MESSAGES.CreateFailure;

    if (isEditMode) {
      action = updateUser({ id, ...payload });
      successMsg = MESSAGES.UpdateSuccess;
      failureMsg = MESSAGES.UpdateFailure;
    }

    dispatch(action).then(({ payload: response }) => {
      if (!response || response.status !== 'OK') {
        const message = response?.message || failureMsg;
        dispatch(showErrorMessage({ message, duration: 5000 }));

        return { [FORM_ERROR]: message };
      }

      dispatch(showSuccessMessage(successMsg));
      dispatch(getUserManagementUserList());

      return resetForm();
    });
  };

  const validateNewUser = (values) => {
    const errors = {};

    if (!values.username) {
      errors.username = MESSAGES.FormFieldRequiredError;
    } else {
      const isUserNameLengthValid = values.username.length > 4;
      if (!isUserNameLengthValid) {
        errors.username = MESSAGES.FormFieldUsernameLengthError;
      }
    }

    if (!values.email) {
      errors.email = MESSAGES.FormFieldRequiredError;
    } else {
      const emailPattern = /\S+@\S+\.\S+/;
      const isEmailValid = emailPattern.test(values.email);
      if (!isEmailValid) {
        errors.email = MESSAGES.FormFieldEmailError;
      }
    }

    return errors;
  };

  const getGridComponents = () => ({
    Toolbar: () =>
      Toolbar({
        openNewUserDialog: () => {
          dispatch(getUserManagementRoleList());
          setIsNewUserDialogOpen(true);
        },
        permissions,
      }),
  });

  const gridComponentProps = {
    toolbar: {
      csvOptions: { disableToolbarButton: true },
      printOptions: { disableToolbarButton: true },
      showQuickFilter: false,
      quickFilterProps: { debounceMs: 500 },
    },
  };

  const onRoleChangeHandler = (values) => {
    const newFormValue = {
      ...formValues,
      roles: values,
    };

    setFormValues(newFormValue);
  };

  const onGridRowClick = ({ row }) => {
    setIsEditMode(true);
    setFormValues(row);

    dispatch(getUserManagementRoleList());

    setIsNewUserDialogOpen(true);
  };

  const getGridColumns = () => [
    {
      field: 'id',
      headerName: 'ID',
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: 'email',
      headerName: 'User',
      headerAlign: 'center',
      width: 250,
      renderCell: ({ row, value }) => (
        <ListItem>
          <ListItemText
            primary={row.username}
            secondary={value}
          />
        </ListItem>
      ),
    },
    {
      field: 'roles',
      headerName: 'Roles',
      flex: 1,
      renderCell: ({ value }) =>
        value.map(({ roleName }) => roleName).join(', '),
    },
  ];

  const handlePreSubmit = (e) => {
    e.preventDefault();
    setOpenDialog({ show: true }); // open the confirmation dialog if prod
  };

  return (
    <ApplicationPageTemplate
      title="User Management - Users"
      description="List of users in the system"
      pageReady>
      <DataGrid
        getRowHeight={() => 'auto'}
        loading={isLoadingUserList}
        rows={userList}
        rowsPerPageOptions={[10]}
        pageSize={10}
        disableColumnFilter
        disableColumnMenu
        disableSelectionOnClick
        disableColumnSelector
        disableDensitySelector
        components={getGridComponents()}
        componentsProps={gridComponentProps}
        onRowClick={onGridRowClick}
        columns={getGridColumns()}
      />
      <Dialog
        fullWidth
        open={isNewUserDialogOpen}>
        <DialogTitle>
          {isEditMode ? 'Update Existing User' : 'Add New User'}
        </DialogTitle>
        <Form
          onSubmit={createNewUser}
          initialValues={isEditMode ? formValues : {}}
          validate={validateNewUser}
          mutators={{
            setValue: ([field, value], state, { changeValue }) => {
              changeValue(state, field, () => value);
            },
          }}
          render={({ submitError, handleSubmit, values, submitting }) => (
            <form
              onSubmit={(e) =>
                process.env.REACT_APP_ENV !== 'production'
                  ? handleSubmit(e)
                  : handlePreSubmit(e)
              }
              noValidate>
              <ProdConfirmationDialog
                openDialog={openDialog}
                yesHandler={handleSubmit}
              />
              <DialogContent style={{ minHeight: '185px' }}>
                <Box sx={{ marginTop: '10px', minHeight: '80px' }}>
                  <TextField
                    label="Username"
                    name="username"
                    required
                    fullWidth
                  />
                  <TextField
                    label="Email"
                    name="email"
                    required
                    fullWidth
                    type="email"
                    style={{ marginTop: 30 }}
                  />
                  <Box style={{ marginTop: 30 }}>
                    {isLoadingRoleList ? (
                      <MultiSelectLoadingContainer>
                        <Typography color="#666666">Select roles</Typography>
                        <Tooltip title="Loading list of roles...">
                          <DownloadingIcon />
                        </Tooltip>
                      </MultiSelectLoadingContainer>
                    ) : (
                      <MultiSelect
                        id="roleOptions"
                        name="roles"
                        items={userManagementRolesList}
                        value={values.roles}
                        isOptionEqualToValue={(option, value) =>
                          option.roleName === value.roleName
                        }
                        label="Select roles"
                        multiple
                        optionDisplayKey="roleName"
                        onChange={onRoleChangeHandler}
                      />
                    )}
                  </Box>
                </Box>

                {submitError && (
                  <Alert
                    severity="error"
                    style={{ marginTop: 30 }}>
                    {submitError}
                  </Alert>
                )}
              </DialogContent>
              <DialogActions
                sx={{
                  justifyContent: isEditMode ? 'space-between' : 'flex-end',
                }}>
                {isEditMode && (
                  <Button
                    variant="outlined"
                    onClick={onDeleteUser}
                    startIcon={<DeleteIcon />}
                    disabled={!permissions?.canDeleteUserManagementUser}>
                    Delete User
                  </Button>
                )}
                <Box>
                  <Button
                    variant="text"
                    onClick={resetForm}>
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    type="submit"
                    disabled={
                      !permissions?.canCreateUserManagementUser ||
                      !permissions?.canUpdateUserManagementUser ||
                      submitting
                    }>
                    Submit
                  </Button>
                </Box>
              </DialogActions>
            </form>
          )}
        />
      </Dialog>
    </ApplicationPageTemplate>
  );
}
